// Enhanced MessageForm.js with original styling - PWA removed, bugs fixed
import React, { useState, useEffect, useRef } from 'react';
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

export default function MessageForm({ 
  clientId, 
  recipientType, 
  orderId = null,           
  orderNumber = null,       
  campaignName = null       
}) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      const autoResize = () => {
        textarea.style.height = 'auto';
        textarea.style.height = Math.min(Math.max(textarea.scrollHeight, 40), 120) + 'px';
      };
      
      textarea.addEventListener('input', autoResize);
      autoResize();
      
      return () => textarea.removeEventListener('input', autoResize);
    }
  }, [text]);

  // Handle paste events for multiple images
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles = [];
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.type.startsWith('image/')) {
          const imageFile = item.getAsFile();
          if (imageFile) {
            const timestamp = Date.now();
            const extension = imageFile.type.split('/')[1] || 'png';
            const fileName = `pasted-image-${timestamp}-${i}.${extension}`;
            
            const namedFile = new File([imageFile], fileName, { type: imageFile.type });
            imageFiles.push(namedFile);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        setFiles(prevFiles => [...prevFiles, ...imageFiles]);
      }
    };

    textarea.addEventListener('paste', handlePaste);
    return () => textarea.removeEventListener('paste', handlePaste);
  }, []);

  // Load admin users for internal messages
  useEffect(() => {
    const loadAdminUsers = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || recipientType !== 'internal') return;

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists() || !['admin', 'facebookAdmin'].includes(userSnap.data().role)) {
          return;
        }

        try {
          const adminListSnap = await getDocs(collection(db, 'adminList'));
          if (!adminListSnap.empty) {
            const admins = adminListSnap.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data()
              }))
              .filter(admin => admin.id !== user.uid);
            
            setAdminUsers(admins);
            return;
          }
        } catch (err) {
          console.log('adminList collection not found, using fallback method');
        }

        const knownAdminUids = [
          'I1izwpyjiDZJpBVxmLBHDvpdGhk2',
          'JNxHzEEaeHV4PHfa529ZPIlyzR12',
          'KRtNcs8208bWzpQVjTH8zkb2QU03',
          'PGBiFT9ZJJaKv9bNnWhqeQ69Dj53',
          'w2lTIPFl6ucvD6GVmPFLpYm1HVz1', 
          'xz5q0ZrhJreRssXN6iNgg6jyDKy1',
          'Ywr4iD7NsfYWDJcMZeViqleddZD2',
          '7aRRTn8032QfyvJgvis0YLOJEzH2', 
        ];

        const adminUsers = [];
        
        for (const adminUid of knownAdminUids) {
          if (adminUid === user.uid) continue;
          
          try {
            const adminSnap = await getDoc(doc(db, 'users', adminUid));
            if (adminSnap.exists()) {
              const adminData = adminSnap.data();
              if (['admin', 'facebookAdmin'].includes(adminData.role)) {
                adminUsers.push({
                  id: adminUid,
                  name: adminData.name || adminData.displayName || 'Unknown Admin',
                  role: adminData.role
                });
              }
            }
          } catch (err) {
            console.warn(`Could not load admin ${adminUid}:`, err);
          }
        }
        
        setAdminUsers(adminUsers);
      } catch (err) {
        console.warn('Could not load admin list:', err);
      }
    };

    loadAdminUsers();
  }, [recipientType]);

    // --- helpers for file handling ---
const getExt = (name = '') => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
};

const sanitizeForPath = (name = '') =>
  name.replace(/[^\w.-]/g, '_');

const getCategory = (contentType = '', ext = '') => {
  const ct = (contentType || '').toLowerCase();
  const e = (ext || '').toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf' || e === 'pdf') return 'pdf';
  if (ct.startsWith('video/')) return 'video';
  if (ct.startsWith('audio/')) return 'audio';
  if (ct === 'text/html' || e === 'html' || e === 'htm') return 'html';
  if (ct.startsWith('text/') || ct === 'application/json' || ['txt','json','csv','md','log'].includes(e)) return 'text';
  return 'other';
};

  const handleSubmit = async e => {
    e.preventDefault();
    if (!text.trim() && files.length === 0) return;

    setSending(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      let senderName = user.displayName || '';
      let senderRole = 'client';

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          senderName = userData.displayName || userData.name || senderName;
          senderRole = userData.role || 'client';
        }
      } catch (err) {
        console.warn('Could not load user data:', err);
      }


    const attachments = [];

const MAX_TOTAL_BYTES = 100 * 1024 * 1024;
const totalBytes = files.reduce((sum, f) => sum + (f?.size || 0), 0);
if (totalBytes > MAX_TOTAL_BYTES) {
  alert('Attachments are too large in total. Please keep under 100 MB.');
  setSending(false);
  return;
}


if (files.length > 0) {
  for (const file of files) {
    // Ensure we carry a contentType and extension for correct previews
    const ext = getExt(file.name);
    const contentType = file.type || (
      ext === 'html' ? 'text/html' :
      ext === 'txt'  ? 'text/plain' :
      ext === 'csv'  ? 'text/csv' :
      ext === 'json' ? 'application/json' :
      ext === 'pdf'  ? 'application/pdf' :
      'application/octet-stream'
    );

    const safeName = sanitizeForPath(file.name);
    const uniqueId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const storagePath = `attachments/${clientId || 'general'}/${uniqueId}_${safeName}`;
    const fileRef = ref(storage, storagePath);

    // Upload with contentType metadata
    await uploadBytes(fileRef, file, { contentType });
    const downloadURL = await getDownloadURL(fileRef);

    attachments.push({
      url: downloadURL,
      name: file.name,
      contentType,
      size: file.size,
      ext,
      category: getCategory(contentType, ext)
    });
  }
}


      // Prepare message data
      const messageData = {
        text: text.trim(),
        senderId: user.uid,
        senderName,
        senderRole,
        recipientType,
        createdAt: serverTimestamp(),
        read: false,
        // Add order context
        ...(orderId && {
          orderId,
          orderNumber: orderNumber || 'Unknown',
          campaignName: campaignName || '',
          isOrderMessage: true
        })
      };

      // Add attachment data
if (attachments.length > 0) {
  messageData.attachments = attachments;
  messageData.hasMultipleAttachments = attachments.length > 1;

  if (attachments.length === 1) {
    const a = attachments[0];
    // legacy fields so existing UI keeps working
    messageData.attachmentUrl = a.url;
    messageData.attachmentName = a.name;
    messageData.attachmentType = a.contentType;
    messageData.isImage = a.category === 'image';
  }
}


      // Add specific recipient if one was selected
      if (selectedRecipient) {
        const selectedAdmin = adminUsers.find(admin => admin.id === selectedRecipient);
        messageData.specificRecipientId = selectedRecipient;
        messageData.specificRecipientName = selectedAdmin?.name || 'Unknown Admin';
      }

      // Create message document
      const msgsRef = collection(db, 'clients', clientId, 'messages');
      await addDoc(msgsRef, messageData);



      console.log(`Message sent ${orderId ? 'for order ' + orderNumber : 'to client'}`);

      // Clear form
      setText('');
      setFiles([]);
      setSelectedRecipient('');

    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
    }
  };

  const removeFile = (indexToRemove) => {
  setFiles(prev => {
    const next = prev.filter((_, i) => i !== indexToRemove);
    if (next.length === 0 && fileInputRef.current) fileInputRef.current.value = '';
    return next;
  });
};


  const removeAllFiles = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isDisabled = sending || (!text.trim() && files.length === 0);
  const showToDropdown = recipientType === 'internal' && adminUsers.length > 0;




  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      {/* Show order context if available */}
      {orderId && (
        <div style={orderContextStyle}>
          <span style={orderTagStyle}>
            📋 Order: {orderNumber} {campaignName && `- ${campaignName}`}
          </span>
        </div>
      )}

      {/* Optional "To" dropdown for admin users */}
      {showToDropdown && (
        <div style={toSectionStyle}>
          <label style={labelStyle}>To (optional):</label>
          <select 
            value={selectedRecipient}
            onChange={(e) => setSelectedRecipient(e.target.value)}
            disabled={sending}
            style={selectStyle}
          >
            <option value="">All Internal Team</option>
            {adminUsers.map(admin => (
              <option key={admin.id} value={admin.id}>
                {admin.name} ({admin.role})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Multiple files attachment area */}
      {files.length > 0 && (
        <div style={filesContainerStyle}>
          <div style={filesHeaderStyle}>
            <span style={filesCountStyle}>{files.length} file{files.length > 1 ? 's' : ''} attached</span>
            <button
              type="button"
              onClick={removeAllFiles}
              disabled={sending}
              style={removeAllButtonStyle}
              title="Remove all files"
            >
              Clear All
            </button>
          </div>
          
          <div style={filesGridStyle}>
            {files.map((file, index) => {
              const isImage = file.type.startsWith('image/');
              const previewUrl = isImage ? URL.createObjectURL(file) : null;
              
              return (
                <div key={index} style={fileItemStyle}>
                  <div style={fileContentStyle}>
                    {isImage && previewUrl ? (
                      <div style={imagePreviewContainerStyle}>
                        <img 
                          src={previewUrl} 
                          alt="Preview of selected file" 
                          style={imagePreviewStyle}
                        />
                        <div style={imageInfoStyle}>
                          <span style={fileNameStyle}>{file.name}</span>
                          <span style={fileSizeStyle}>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={fileInfoStyle}>
                        <span style={fileIconStyle}>📄</span>
                        <div style={fileDetailsStyle}>
                          <span style={fileNameStyle}>{file.name}</span>
                          <span style={fileSizeStyle}>
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    disabled={sending}
                    style={removeFileButtonStyle}
                    title={`Remove ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      

      {/* Main input area */}
      <div style={inputContainerStyle}>
        <label style={attachButtonStyle}>
          <span style={attachIconStyle}>📎</span>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            disabled={sending}
            accept=".txt,.html,.json,.csv,application/pdf,text/plain,text/html,text/csv,application/json,image/*,audio/*,video/*"
            multiple
            style={{ display: 'none' }}
          />
        </label>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={`Type a message${orderId ? ' about this order' : ''}...`}
          rows="1"
          disabled={sending}
          style={textareaStyle}
        />

        <button 
          type="submit" 
          disabled={isDisabled}
          style={{
            ...sendButtonStyle,
            backgroundColor: isDisabled ? '#ccc' : '#007AFF',
            cursor: isDisabled ? 'not-allowed' : 'pointer'
          }}
          title="Send message"
        >
          {sending ? (
            <div style={loadingSpinnerStyle}></div>
          ) : (
            <span style={{ fontSize: '16px', lineHeight: '1' }}>➤</span>
          )}
        </button>
      </div>
    </form>
  );
}

// ALL ORIGINAL STYLES PRESERVED
const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  border: '1px solid #e0e0e0'
};

const orderContextStyle = {
  padding: '8px 12px',
  backgroundColor: '#e3f2fd',
  borderRadius: '6px',
  border: '1px solid #bbdefb',
  marginBottom: '4px'
};

const orderTagStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1976d2'
};

const toSectionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 8px',
  backgroundColor: '#e8f4f8',
  borderRadius: '6px',
  border: '1px solid #b3d9e6'
};

const labelStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: '#2c5aa0',
  minWidth: 'fit-content'
};

const selectStyle = {
  flex: 1,
  padding: '6px 8px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '14px',
  backgroundColor: 'white',
  outline: 'none'
};

const filesContainerStyle = {
  padding: '8px',
  backgroundColor: '#e3f2fd',
  borderRadius: '8px',
  border: '1px solid #bbdefb'
};

const filesHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px'
};

const filesCountStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: '#1976d2'
};

const removeAllButtonStyle = {
  background: 'none',
  border: '1px solid #f44336',
  fontSize: '12px',
  color: '#f44336',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
  transition: 'all 0.2s ease'
};

const filesGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '8px'
};

const fileItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '8px',
  backgroundColor: 'white',
  borderRadius: '6px',
  border: '1px solid #ddd'
};

const fileContentStyle = {
  flex: 1,
  minWidth: 0
};

const imagePreviewContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const imagePreviewStyle = {
  width: '50px',
  height: '50px',
  borderRadius: '4px',
  objectFit: 'cover',
  border: '1px solid #ddd',
  flexShrink: 0
};

const imageInfoStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0
};

const fileInfoStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0
};

const fileDetailsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0
};

const fileSizeStyle = {
  fontSize: '12px',
  color: '#666'
};

const fileIconStyle = {
  fontSize: '16px',
  flexShrink: 0
};

const fileNameStyle = {
  fontSize: '14px',
  color: '#1976d2',
  fontWeight: '500',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const removeFileButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '18px',
  color: '#f44336',
  cursor: 'pointer',
  padding: '0',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const inputContainerStyle = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  padding: '4px'
};

const attachButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  backgroundColor: '#f0f0f0',
  borderRadius: '18px',
  cursor: 'pointer',
  border: '1px solid #ddd',
  transition: 'background-color 0.2s ease',
  flexShrink: 0
};

const attachIconStyle = {
  fontSize: '16px',
  color: '#666'
};

const textareaStyle = {
  flex: 1,
  minHeight: '36px',
  maxHeight: '120px',
  padding: '8px 12px',
  border: '1px solid #ddd',
  borderRadius: '18px',
  fontSize: '14px',
  fontFamily: 'inherit',
  resize: 'none',
  outline: 'none',
  backgroundColor: 'white',
  transition: 'border-color 0.2s ease'
};

const sendButtonStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '18px',
  border: 'none',
  backgroundColor: '#007AFF',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease',
  flexShrink: 0
};

const loadingSpinnerStyle = {
  width: '16px',
  height: '16px',
  border: '2px solid #ffffff30',
  borderTop: '2px solid #ffffff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite'
};